import type { ForecastPoint } from '@erp/rpc';
import type { NeedSalesPoint } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

/**
 * The Line footer's Sales tab (GEN-3285): one column per month of the last
 * twelve, this month last, with the same month a year earlier above it and
 * the forecast below: this month's own forecast under this month, and next
 * year's under every past month, so the row covers the twelve months from
 * now. A buyer reads down a column to see a month against last year and
 * against what is expected.
 */
export interface LineSalesRow {
  key: 'prior' | 'recent' | 'forecast';
  /**
   * One per column. Sales history is sparse (no entry for a month with no
   * sales), so a sales row reads 0 there; a forecast row reads null for a
   * month with no forecast.
   */
  cells: (number | null)[];
  /** Sum of the known cells; null when none is known. */
  total: number | null;
}

export interface LineSalesTable {
  /** `YYYY-MM` of each column's recent month, oldest first. */
  months: string[];
  rows: LineSalesRow[];
}

/** `YYYY-MM` of the month `offset` months from `month` (`YYYY-MM`). */
export function shiftMonth(month: string, offset: number): string {
  const [year, mon] = month.split('-').map(Number);
  const index = year * 12 + (mon - 1) + offset;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
}

const monthFormats = new Map<string, Intl.DateTimeFormat>();

/** The short month name of `YYYY-MM` in the viewer's language. */
export function monthLabel(month: string, locale: string): string {
  let format = monthFormats.get(locale);
  if (format === undefined) {
    format = new Intl.DateTimeFormat(locale, {
      month: 'short',
      timeZone: 'UTC',
    });
    monthFormats.set(locale, format);
  }
  return format.format(new Date(`${month}-01T00:00:00Z`));
}

function byMonth(
  points: readonly { monthStart: string; quantity: number }[],
): Map<string, number> {
  return new Map(points.map((p) => [p.monthStart.slice(0, 7), p.quantity]));
}

/** `sources` holds the month each column reads from `values`. */
function row(
  key: LineSalesRow['key'],
  sources: string[],
  values: Map<string, number>,
  missing: number | null,
): LineSalesRow {
  const cells = sources.map((m) => values.get(m) ?? missing);
  const known = cells.filter((c): c is number => c !== null);
  return {
    key,
    cells,
    total: known.length === 0 ? null : known.reduce((a, b) => a + b, 0),
  };
}

export function lineSalesTable(
  history: readonly NeedSalesPoint[],
  forecast: readonly ForecastPoint[],
  now: Date,
): LineSalesTable {
  // The server buckets sales by UTC month (see lineFacts.salesFacts).
  const current = now.toISOString().slice(0, 7);
  const months = Array.from({ length: 12 }, (_, i) =>
    shiftMonth(current, i - 11),
  );
  const sales = byMonth(history);
  return {
    months,
    rows: [
      row(
        'prior',
        months.map((m) => shiftMonth(m, -12)),
        sales,
        0,
      ),
      row('recent', months, sales, 0),
      row(
        'forecast',
        months.map((m) => (m === current ? m : shiftMonth(m, 12))),
        byMonth(forecast),
        null,
      ),
    ],
  };
}
